<script setup lang="ts">
/**
 * Renders one validated ChartSpec with Chart.js. Chart.js is loaded
 * lazily (dynamic import) the first time a chart actually appears, so the
 * library stays out of the initial bundle — most chats have no charts.
 *
 * UX/a11y best-practice baked in:
 *  - a screen-reader-visible <table> mirrors the data (a canvas chart is
 *    invisible to assistive tech otherwise);
 *  - a "Show data" toggle reveals that table for sighted users too;
 *  - "PNG" exports the rendered canvas;
 *  - colours come from resolved CSS theme vars, so dark mode works;
 *  - if Chart.js fails to load/render we fall back to the data table —
 *    never a blank box.
 */
import { onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";
import type { ChartSpec } from "../utils/chartSpec";
import { toChartJsConfig } from "../utils/chartSpec";

const props = defineProps<{ spec: ChartSpec }>();

const canvasEl = ref<HTMLCanvasElement | null>(null);
const showData = ref(false);
const failed = ref(false);
// shallowRef: the Chart instance is a big external object we don't want Vue
// to deeply reactive-proxy.
const chart = shallowRef<{
  destroy: () => void;
  toBase64Image: () => string;
} | null>(null);

function readTheme(): { ink: string; grid: string } {
  const styles = getComputedStyle(document.documentElement);
  const ink = styles.getPropertyValue("--color-text").trim() || "#1a1a1a";
  const border =
    styles.getPropertyValue("--color-border").trim() || "rgba(120,120,120,0.2)";
  return { ink, grid: border };
}

async function render() {
  if (!canvasEl.value) return;
  try {
    // Auto-registers all controllers/scales/elements — the simplest correct
    // setup; tree-shaking the registry isn't worth the footgun here.
    const { default: Chart } = await import("chart.js/auto");
    if (chart.value) {
      chart.value.destroy();
      chart.value = null;
    }
    const cfg = toChartJsConfig(props.spec, readTheme());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    chart.value = new Chart(canvasEl.value, cfg as any) as any;
    failed.value = false;
  } catch (err) {
    console.error("[ChartBlock] render failed", err);
    failed.value = true;
  }
}

function exportPng() {
  const c = chart.value;
  if (!c) return;
  const url = c.toBase64Image();
  const a = document.createElement("a");
  a.href = url;
  const name = (props.spec.title || "chart")
    .replace(/[^a-z0-9]+/gi, "-")
    .toLowerCase();
  a.download = `${name}.png`;
  a.click();
}

onMounted(render);
watch(() => props.spec, render, { deep: true });
onBeforeUnmount(() => {
  chart.value?.destroy();
  chart.value = null;
});
</script>

<template>
  <figure class="chartblk">
    <div v-if="!failed" class="chartblk__canvas-wrap">
      <canvas
        ref="canvasEl"
        role="img"
        :aria-label="spec.title || 'Chart'"
      ></canvas>
    </div>

    <div class="chartblk__bar">
      <span v-if="spec.title" class="chartblk__title">{{ spec.title }}</span>
      <span class="chartblk__spacer"></span>
      <button type="button" class="chartblk__btn" @click="showData = !showData">
        {{ showData ? "Hide data" : "Show data" }}
      </button>
      <button
        v-if="!failed"
        type="button"
        class="chartblk__btn"
        @click="exportPng"
      >
        PNG
      </button>
    </div>

    <!-- Always in the DOM for screen readers; visually collapsed unless
         toggled (or shown as the fallback when the canvas failed). -->
    <div
      class="chartblk__data"
      :class="{ 'chartblk__data--visible': showData || failed }"
    >
      <table>
        <caption v-if="failed">
          Chart could not be rendered — showing the data.
        </caption>
        <thead>
          <tr>
            <th scope="col">
              {{ spec.labels.length ? spec.labelTitle || "Category" : "" }}
            </th>
            <th v-for="(ds, i) in spec.datasets" :key="i" scope="col">
              {{ ds.label || `Series ${i + 1}` }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(lbl, r) in spec.labels" :key="r">
            <th scope="row">{{ lbl }}</th>
            <td v-for="(ds, c) in spec.datasets" :key="c">{{ ds.data[r] }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </figure>
</template>

<style scoped>
.chartblk {
  margin: 0.75rem 0;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  background: var(--color-surface, transparent);
}
.chartblk__canvas-wrap {
  position: relative;
  height: 260px;
  width: 100%;
}
.chartblk__bar {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.4rem;
}
.chartblk__title {
  font-size: 0.8rem;
  color: var(--color-muted);
}
.chartblk__spacer {
  flex: 1;
}
.chartblk__btn {
  font-size: 0.75rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: 5px;
  background: transparent;
  color: var(--color-ink);
  cursor: pointer;
}
.chartblk__btn:hover {
  background: var(--color-border);
}
.chartblk__data {
  display: none;
  margin-top: 0.5rem;
  overflow-x: auto;
}
.chartblk__data--visible {
  display: block;
}
.chartblk__data table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}
.chartblk__data th,
.chartblk__data td {
  border: 1px solid var(--color-border);
  padding: 0.25rem 0.5rem;
  text-align: left;
}
.chartblk__data caption {
  font-size: 0.78rem;
  color: var(--color-muted);
  text-align: left;
  margin-bottom: 0.3rem;
}
</style>
